import { Component, NgZone, ViewChild } from "@angular/core";
import { EventInfoComponent } from "../event-info.component";
import { combineLatest, Observable, timer } from "rxjs";
import { debounce, map, shareReplay, startWith, take } from "rxjs/operators";
import { deepReplace, UtilsService } from "projects/common/src/public-api";
import { ModalBehavior, ModalsService } from "projects/common/src/lib/services/modals.service";
import { BusinessService } from "../../../services/business.service";
import { ActivatedRoute, Router } from "@angular/router";
import { UsersService } from "../../../services/users.service";
import { MatLegacySnackBar } from "@angular/material/legacy-snack-bar";
import { NotesService } from "../../../services/notes.service";
import { ProgressBarService } from "../../../services/progress-bar.service";
import { EstimatesService } from "../../../services/estimates.service";
import { InvoicesService } from "../../../services/invoices.service";
import { ScrollDispatcher } from "@angular/cdk/scrolling";
import { ScheduleService } from "../../../services/schedule.service";
import { ChatService, unseenIndicatorDebounceMs } from "../../../services/chat.service";
import { ViewAsService } from "../../../services/view-as.service";
import { NavigationService } from "../../../services/navigation.service";
import { Assessment } from "../../../models/assessment.model";
import { Job } from "../../../models/jobs.model";
import { ProgressBarStage } from "../../../models/progress-bar.model";
import { clientFromDocument } from "projects/common/src/lib/models/client-transform";
import { EstimateVersion } from "../../../models/estimate.model";
import { ChatComponent } from "../../../components/chat/chat.component";
import { Chat } from '../../../models/chat.model';
import { RequestInvitationFormData, RequestInvitationModalData } from "../../../modals/request-invitation/request-invitation.model";
import { RequestInvitationModal } from "../../../modals/request-invitation/request-invitation.component";

const tabs = ['Info', 'Notes', 'Chat'];

@Component({
  template: ''
})
export abstract class AssessmentOrJobComponent<T extends Assessment | Job> extends EventInfoComponent<T> {
  abstract onRequestInvitationSubmit(original: T, data: Required<RequestInvitationFormData>, create: boolean): Promise<null>;

  @ViewChild('chat') chat!: ChatComponent;

  chat$: Observable<Chat | null> = this.chatService.chatObservable(this.workflowId, true).pipe(shareReplay({bufferSize: 1, refCount: true}));
  progressBar$ = this.progressBarService.progressBarObservable(this.workflowId);

  showChat$ = this.selectedTab$.pipe(
    map((selectedTab) => selectedTab === 'Chat'),
    shareReplay({bufferSize: 1, refCount: true})
  );
  
  tabsRedDots$ = combineLatest([
    this.chat$.pipe(
      map(chat => !!chat?.hasUnseenMessages),
      startWith(false),
      debounce(hasUnseen => timer(hasUnseen ? unseenIndicatorDebounceMs : 0))
    ),
    this.tabs$
  ]).pipe(
    map(([chatHasUnseenMessages, tabs]) => tabs.map(tab => tab === 'Chat' && chatHasUnseenMessages))
  );

  constructor(
    private progressBarService: ProgressBarService,
    private estimatesService: EstimatesService,
    private invoicesService: InvoicesService,
    private chatService: ChatService,
    protected snackbar: MatLegacySnackBar,
    protected scheduleService: ScheduleService,
    modalsService: ModalsService,
    businessService: BusinessService,
    router: Router,
    route: ActivatedRoute,
    usersService: UsersService,
    notesService: NotesService,
    scrollDispatcher: ScrollDispatcher,
    viewAsService: ViewAsService,
    utilsService: UtilsService,
    navigationService: NavigationService,
    zone: NgZone,
  ) {
    super(
      businessService,
      modalsService,
      utilsService,
      router,
      route,
      usersService,
      notesService,
      scrollDispatcher,
      viewAsService,
      navigationService,
      zone,
      window
    );
    this.tabsSubject.next(tabs);
    this.selectedTabSubject.next(tabs[0]);
    this.selectedSideTabSubject.next(tabs.slice(1)[0]);
  }

  async ngOnInit() {
    super.ngOnInit();
    this.chat$.pipe(take(1)).toPromise(); // preload chat
  }

  async setActiveTab(tab: string) {
    const largeScreen = await this.largeScreen$.pipe(take(1)).toPromise();
    largeScreen ? this.selectedSideTabSubject.next(tab) : this.selectedTabSubject.next(tab);
  }

  getStatusToggleClass(document: T) {
    let className = 'event-status';
    className += ' ' + (document.docType === 'Assessment' ? 'width--240' : 'width--248');
    className += ' event-status-' + document.status.toLowerCase();
    return className;
  }

  async createOrOpenEstimate(document: T, estimateStage: ProgressBarStage) {
    let estimateId = estimateStage.id;
    const workflowId = document.workflowId;
    if(!estimateStage.canOpen) {
      const user = await this.usersService.currentUser$.pipe(take(1)).toPromise();
      const client = clientFromDocument(document);

      const res = await this.estimatesService.createEstimate({
        inClient: client,
        inWorkflowId: document.workflowId,
        inCreatedBy: user.id,
      });
      estimateId = res.estimateId;
    }
    await this.router.navigate(['/estimates', workflowId, estimateId]);
  }

  async createOrOpenInvoice(document: T, invoiceStage: ProgressBarStage, estimateStage?: ProgressBarStage) {
    let invoiceId = invoiceStage.id;
    const workflowId = document.workflowId;
    if (!invoiceStage.canOpen) {
      const user = await this.usersService.currentUser$.pipe(take(1)).toPromise();
      const client = clientFromDocument(document);

      let activeEstimateVersion: EstimateVersion | undefined = undefined;
      if (estimateStage && estimateStage.id) {
        const estimate = await this.estimatesService.getEstimate(estimateStage.id);
        activeEstimateVersion = estimate.versions.find(version => version.id === estimate.activeVersionId);
      }

      const res = await this.invoicesService.createInvoice({
        inClient: client,
        inWorkflowId: document.workflowId,
        inCreatedBy: user.id,
        ...(activeEstimateVersion && { inItems: activeEstimateVersion.items ?? undefined }),
        ...(activeEstimateVersion && { inNote: activeEstimateVersion.note ?? undefined }),
        ...(activeEstimateVersion && { inDiscountType: activeEstimateVersion.discountType ?? undefined }),
      });
      invoiceId = res.invoiceId;
    }
    await this.router.navigate(['/invoices', workflowId, invoiceId]);
  }

  async editDocument(document: T) {
    const users = await this.usersService.users$.pipe(take(1)).toPromise();
    const client = clientFromDocument(document as any);
    const replacedClient = deepReplace(client, null, undefined);
    const assignees = this.getAssignees(document);
    const data: RequestInvitationModalData = {
      initialData: {
        id: document.id,
        workflowId: document.workflowId,
        dialogType: document.docType,
        jobType: document.jobType,
        client: replacedClient,
        assignees: assignees.map(assignee => users!.find(user => user.id === assignee)!),
        ranges: document.ranges,
      },
      onSubmit: ((data: Required<RequestInvitationFormData>) => this.onRequestInvitationSubmit(document, data, false)).bind(this),
    };
    this.modalsService.open(RequestInvitationModal, {
      behavior: ModalBehavior.Auto,
      breakpoint: RequestInvitationModal.BREAKPOINT,
      disableClose: true,
      data: data
    });
  }

  sidePanelClick(event: Event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.chat?.onMenuClick(event);
  }

}